import { useEffect, useRef } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { useSavePartVideo } from 'parts/hooks';

import { updateVideoSchema } from './schema';

interface IUpdateVideoFormValues {
  name: string;
  video: string;
}

interface IProps {
  isOpen: boolean;
  partNumber: string;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

const defaultValues: IUpdateVideoFormValues = { name: '', video: '' };

export const UpdateVideo = ({ isOpen, partNumber, onClose, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { savePartVideo } = useSavePartVideo({ position: NotificationPosition.FORM });

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  const onSubmit =
    (reset: UseFormReset<IUpdateVideoFormValues>) => async (data: IUpdateVideoFormValues) => {
      await savePartVideo({
        name: data.name,
        part: partNumber,
        video: data.video,
      });

      closeTimeoutRef.current = setTimeout(() => {
        handleOnClose(reset);
        onSubmitSuccess();
      }, 1000);
    };

  const handleOnClose = (reset: UseFormReset<IUpdateVideoFormValues>) => {
    clearNotification();
    onClose();
    reset(defaultValues);
  };

  return (
    <FormProvider schema={updateVideoSchema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        title={t('titleUpdateVideo')}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        submitBtnLabel={t('buttonLabelUpdateVideo')}
      >
        <FormField
          name="video"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelYoutubeLink') }}
        />
        <FormField
          name="name"
          type={InputType.TEXT_INPUT}
          inputProps={{ label: t('labelTitle') }}
        />
      </Form>
    </FormProvider>
  );
};
